import moment from "moment";
import { displayDateFormat, voucherRejectedAmountForViewer } from "../../../utils/helpers";
import { resolveVoucherListRowId } from "../../../utils/dmsStandaloneListUtils";

/** DMS often sends `21-Apr-2026 01:59` (DD-MMM-YYYY HH:mm). `moment(raw)` alone is deprecated / fragile. */
function formatSubmittedOnCell(raw) {
  if (raw == null || String(raw).trim() === "") return "-";
  const s = String(raw).trim();
  let m = moment(s, "DD-MMM-YYYY HH:mm", true);
  if (!m.isValid()) {
    m = moment(s, "DD-MMM-YYYY H:mm", true);
  }
  if (!m.isValid()) {
    m = moment(s, moment.ISO_8601, true);
  }
  if (!m.isValid()) {
    m = moment(s);
  }
  if (!m.isValid()) return s;
  return m.format(displayDateFormat);
}

export const storeColumns = ({
  navigate,
  navigationStateExtras = {},
}) => [
  {
    Header: "V.ID",
    accessor: "id",
    Cell: (props) => {
      const id = resolveVoucherListRowId(props?.row?.original);
      const allData = props?.row?.original;

      if (!id) {
        return <span>-</span>;
      }

      return (
        <div
          className="cursor-pointer underline underline-offset-4"
          style={{
            color: "blue",
            cursor: "pointer",
            pointerEvents: "auto",
            userSelect: "none",
            position: "relative",
            zIndex: 2,
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof navigate !== "function") return;
            navigate(`/vouchers/${encodeURIComponent(id)}`, {
              state: { ...allData, ...navigationStateExtras },
            });
          }}
        >
          {id}
        </div>
      );
    },
  },
  {
    Header: "Submit On",
    accessor: "submittedOn",
    Cell: (props) => {
      const row = props?.row?.original;
      const raw = row?.submittedOn ?? row?.createdOn;
      if (!raw) return "-";
      return <div>{formatSubmittedOnCell(raw)}</div>;
    },
  },
  {
    Header: "Amount (₹)",
    accessor: "amount",
    Cell: (rowData) => {
      const value = rowData?.row?.original?.amount;
      let amount;
      if (typeof value == "string") {
        amount = Number(value).toFixed(2);
      } else {
        amount = value;
      }
      return Number(amount).toFixed(2) || "-";
    },
  },
  {
    Header: "L1 Status",
    accessor: "l1_status",
    Cell: (props) => {
      const row = props?.row?.original || {};
      const value = row?.l1?.status ?? row?.L1?.status;
      if (!value) {
        return "-";
      }
      return (
        <div
          className={
            value === "Approved"
              ? "text-[var(--successGreen)]"
              : value === "Rejected"
              ? "text-[var(--textRed)]"
              : value === "Pending"
              ? "text-[var(--pendingYellow)]"
              : value === "Pending"
              ? "text-[var(--pendingYellow)]"
              : "text-[var(--pendingYellow)]"
          }
        >
          {value}
        </div>
      );
    },
  },
  {
    Header: "L2 Status",
    accessor: "l2_status",
    Cell: (props) => {
      const row = props?.row?.original || {};
      const value = row?.l2?.status ?? row?.L2?.status;
      if (!value) {
        return "-";
      }
      return (
        <div
          className={
            value === "Approved"
              ? "text-[var(--successGreen)]"
              : value === "Rejected"
              ? "text-[var(--textRed)]"
              : value === "Pending"
              ? "text-[var(--pendingYellow)]"
              : "text-[var(--pendingYellow)]"
          }
        >
          {value}
        </div>
      );
    },
  },
  {
    Header: "A/C Status",
    accessor: "ac_status",
    Cell: (props) => {
      const row = props?.row?.original || {};
      const value = row?.l3?.status ?? row?.L3?.status;
      if (!value) {
        return "-";
      }
      return (
        <div
          className={
            value === "Approved"
              ? "text-[var(--successGreen)]"
              : value === "Rejected"
              ? "text-[var(--textRed)]"
              : value === "Pending"
              ? "text-[var(--pendingYellow)]"
              : "text-[var(--pendingYellow)]"
          }
        >
          {value}
        </div>
      );
    },
  },
  {
    Header: "Approved Amount (₹)",
    accessor: "approved_amount",
    Cell: (props) => {
      const row = props?.row?.original || {};
      const ac = row?.l3 ?? row?.L3;
      const value = ac?.approvedAmount
        ? Number(ac.approvedAmount).toFixed(2)
        : "-";
      return <div className={"text-[var(--successGreen)]"}>{value}</div>;
    },
  },
];

export const l1Columns = ({ extraColumns = [], userLabel = "L1", navigate }) => [
  ...extraColumns,
  {
    Header: "Submit On",
    accessor: "submittedOn",
    Cell: (props) => {
      const raw = props?.row?.original?.submittedOn;
      if (!raw) return "-";
      return <div>{formatSubmittedOnCell(raw)}</div>;
    },
  },
  {
    Header: "Amount (₹)",
    accessor: "amount",
    Cell: (rowData) => {
      const value = rowData?.row?.original?.amount;
      let amount;
      if (typeof value == "string") {
        amount = Number(value).toFixed(2);
      } else {
        amount = value;
      }
      return amount ? Number(amount).toFixed(2) : "-";
    },
  },
  {
    Header: "Approved",
    accessor: "approved",
    Cell: (props) => {
      const row = props?.row?.original || {};
      // L1: L1-frozen total. L2: parent `approvedAmount` (L2-committed rupees), not l1AmountApproved.
      // Finance: L3/finance-committed on parent. (Normalize label — useMemo without userLabel was fixed in AllVouchers;
      // toLowerCase catches whitespace/spelling variants from auth.)
      const ul = String(userLabel ?? "")
        .trim()
        .toLowerCase();
      let value;
      if (ul === "l1") {
        value = row.l1AmountApproved ?? row.l1_amount_approved ?? row.approvedAmount;
      } else if (ul === "l2") {
        value = row.approvedAmount;
      } else if (ul === "finance" || ul === "l3" || ul === "accounts") {
        value = row.l3AmountApproved ?? row.l3_amount_approved ?? row.approvedAmount;
      } else {
        value = row.l1AmountApproved ?? row.l1_amount_approved ?? row.approvedAmount;
      }
      if (value == null || value === "" || Number(value) === 0) {
        return "-";
      }
      return (
        <div className={"text-[var(--successGreen)]"}>
          {Number(value).toFixed(2)}
        </div>
      );
    },
  },
  {
    Header: "Pending",
    accessor: "pendingAmount",
    Cell: (props) => {
      const value = props?.row?.original?.pendingAmount;
      if (!value) {
        return "-";
      }
      return (
        <div className={"text-[var(--pendingYellow)]"}>
          {value ? Number(value).toFixed(2) : "-"}
        </div>
      );
    },
  },
  {
    Header: "Rejected",
    accessor: "rejectedAmount",
    Cell: (props) => {
      const value = voucherRejectedAmountForViewer(props?.row?.original, userLabel);
      if (value == null || value === "" || Number(value) === 0) {
        return "-";
      }
      return (
        <div className={"text-[var(--textRed)]"}>
          {Number(value).toFixed(2)}
        </div>
      );
    },
  },
];
